import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServerError } from '../../../types';
import { request } from '../../../utils/helpers/httpHelpers';
import {
  UserProfile,
  userProfilesApi,
} from '@texas/api/endpoints/userProfilesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';

interface MergeUserProfileDialogProps {
  userProfile: UserProfile | null;
  availableProfiles: UserProfile[];
  isOpen: boolean;
  onClose: () => void;
  onMerge: (profile: UserProfile) => void;
}

export function MergeUserProfileDialog(props: MergeUserProfileDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [targetProfileId, setTargetProfileId] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const { request: mergeRequest, loading } = useApiRequest(
    userProfilesApi.mergeUserProfile,
  );

  const handleMerge = async () => {
    if (!props.userProfile || !targetProfileId) return;

    await request(
      mergeRequest,
      [props.userProfile.id, targetProfileId],
      (profile: UserProfile) => {
        toast({
          title: t('userProfile.merged'),
          description: t('userProfile.mergeSuccess', {
            source: props.userProfile?.name,
            target: profile.name,
          }),
          status: 'success',
          isClosable: true,
        });
        props.onMerge(profile);
        props.onClose();
        setTargetProfileId(null);
        setSelectedOption(null);
      },
      (error: ServerError) => {
        toast({
          title: t('userProfile.mergeFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  const targetOptions = props.availableProfiles
    .filter(
      (p) =>
        p.id !== props.userProfile?.id &&
        p.type === props.userProfile?.type &&
        !p.archived,
    )
    .map((p) => ({ label: p.name, value: p.id }));

  useEffect(() => {
    if (!props.isOpen) {
      setTargetProfileId(null);
      setSelectedOption(null);
    }
  }, [props.isOpen]);

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={props.isOpen}
      onClose={props.onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{t('userProfile.mergeProfile')}</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Box>
              {t('userProfile.mergeDescription', {
                name: props.userProfile?.name,
              })}
            </Box>
            <FormControl mt={4}>
              <FormLabel>{t('userProfile.targetProfile')}</FormLabel>
              <TexasSelect
                options={targetOptions}
                value={selectedOption}
                placeholder={t('userProfile.selectTargetProfile')}
                onChange={(option) => {
                  setSelectedOption(option);
                  setTargetProfileId(option?.value ?? null);
                }}
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={props.onClose}
              variant="texas-light"
            >
              {t('general.cancel')}
            </Button>
            <Button
              onClick={handleMerge}
              ml={3}
              variant="texas-solid"
              isLoading={loading}
              isDisabled={!targetProfileId}
            >
              {t('userProfile.merge')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
